import React, { useCallback, useEffect, useState } from "react";
import { Link2, Check, X, Trash2, Loader2, Send } from "lucide-react";

// Broker self-service: request access to another brokerage's clients, and
// accept/decline requests from other brokers to access yours.
export default function BrokerLinks({ authAxios, onChange }) {
  const [directory, setDirectory] = useState([]);
  const [links, setLinks] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, l, i] = await Promise.all([
        authAxios.get("/broker/access/directory"),
        authAxios.get("/broker/access/links"),
        authAxios.get("/broker/access/incoming"),
      ]);
      setDirectory(d.data?.brokers || []);
      setLinks(l.data?.links || []);
      setIncoming(i.data?.requests || []);
    } catch { /* not a broker / no access */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const request = async () => {
    if (!target) return;
    setBusy(true); setMsg("");
    try {
      const r = await authAxios.post("/broker/access/request", { target_broker_id: target });
      setMsg(r.data?.status === "active" ? "✓ Linked." : "✓ Request sent — awaiting the other brokerage's approval.");
      setTarget("");
      await load();
    } catch (e) { setMsg("⚠ " + (e?.response?.data?.detail || "Request failed.")); }
    setBusy(false);
  };

  const resolve = async (id, action) => {
    setBusy(true); setMsg("");
    try {
      await authAxios.post(`/broker/access/incoming/${id}/${action}`);
      await load();
      if (onChange) onChange();
    } catch (e) { setMsg("⚠ " + (e?.response?.data?.detail || "Action failed.")); }
    setBusy(false);
  };

  const removeLink = async (id) => {
    if (!window.confirm("Remove this access link?")) return;
    setBusy(true);
    try { await authAxios.delete(`/broker/access/links/${id}`); await load(); if (onChange) onChange(); }
    catch (e) { setMsg("⚠ " + (e?.response?.data?.detail || "Failed.")); }
    setBusy(false);
  };

  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="broker-links">
      <div className="flex items-center gap-2 mb-1">
        <Link2 size={16} className="text-[#C9A227]" />
        <h3 className="font-serif text-xl">Linked Brokerages</h3>
      </div>
      <p className="text-[12px] text-[#8A8A8A] mb-4 max-w-2xl">Request access to a partner brokerage's clients, or approve a partner who wants to co-manage yours. Linked brokerages can view and edit each other's borrower files. Your clients stay private from every other broker.</p>

      {/* Request access */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <label className="flex-1 min-w-[220px]">
          <span className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Request access to a brokerage</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="broker-links-target"
            className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#C9A227]">
            <option value="">— Select a brokerage —</option>
            {directory.map((b) => <option key={b.id} value={b.id}>{b.company || b.contact_name || b.id.slice(0, 8)}</option>)}
          </select>
        </label>
        <button onClick={request} disabled={busy || !target} data-testid="broker-links-request-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#C9A227] text-[#0A0A0A] text-[13px] font-semibold hover:bg-[#e0b93a] disabled:opacity-40 transition-colors">
          <Send size={13} /> Request
        </button>
      </div>
      {msg && <div className="mb-4 text-[12px] text-[#B7E4D0]" data-testid="broker-links-msg">{msg}</div>}

      {/* Incoming requests */}
      {incoming.filter((g) => g.status === "pending").length > 0 && (
        <div className="mb-5" data-testid="broker-links-incoming">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#E4C97A] font-bold mb-2">Requests to access your clients</div>
          <div className="space-y-2">
            {incoming.filter((g) => g.status === "pending").map((g) => (
              <div key={g.id} className="flex items-center gap-3 text-[12.5px] bg-[#1A1606] border border-[#C9A227]/30 rounded-md px-3 py-2" data-testid={`broker-links-incoming-${g.id}`}>
                <span className="text-[#ECECE4]">{g.grantee_name}</span>
                <span className="text-[#5C5C5C]">requests access</span>
                <div className="ml-auto flex gap-2">
                  <button onClick={() => resolve(g.id, "accept")} disabled={busy} data-testid={`broker-links-accept-${g.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#3DBB8E] text-[#0A0A0A] text-[11px] font-semibold hover:bg-[#5fd6ab]"><Check size={12} /> Accept</button>
                  <button onClick={() => resolve(g.id, "decline")} disabled={busy} data-testid={`broker-links-decline-${g.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#FF8585]/50 text-[#FF8585] text-[11px] hover:bg-[#FF8585]/10"><X size={12} /> Decline</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My outgoing links */}
      <div className="text-[11px] uppercase tracking-[0.12em] text-[#9A9A90] font-bold mb-2">Brokerages you can access</div>
      {loading ? (
        <div className="flex items-center gap-2 text-[#5C5C5C] text-[12px]"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : links.length === 0 ? (
        <div className="text-[12px] text-[#5C5C5C]" data-testid="broker-links-empty">No linked brokerages yet.</div>
      ) : (
        <div className="space-y-2">
          {links.map((g) => (
            <div key={g.id} className="flex items-center gap-3 text-[12.5px] py-1.5 border-b border-[#2A2A22] last:border-0" data-testid={`broker-links-row-${g.id}`}>
              <span className="text-[#ECECE4]">{g.target_name}</span>
              <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.1em] font-bold ${g.status === "active" ? "bg-[#3DBB8E]/15 text-[#5fd6ab] border border-[#0B6B4F]/50" : "bg-[#B8860B]/10 text-[#E4C97A] border border-[#B8860B]/40"}`}>{g.status === "active" ? "Linked" : "Pending"}</span>
              <button onClick={() => removeLink(g.id)} disabled={busy} data-testid={`broker-links-remove-${g.id}`}
                className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#FF8585]/40 text-[#FF8585] text-[11px] hover:bg-[#FF8585]/10"><Trash2 size={12} /> Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
